export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  min?: number;
  max?: number;
  custom?: (value: unknown) => string | null;
}

export interface ValidationErrors {
  [key: string]: string;
}

export function validateField(
  value: unknown,
  rules: ValidationRule
): string | null {
  // Required check
  if (rules.required && !value) {
    return 'This field is required';
  }

  if (!value && !rules.required) {
    return null;
  }

  const stringValue = String(value);

  // Min length check
  if (rules.minLength && stringValue.length < rules.minLength) {
    return `Minimum length is ${rules.minLength} characters`;
  }

  // Max length check
  if (rules.maxLength && stringValue.length > rules.maxLength) {
    return `Maximum length is ${rules.maxLength} characters`;
  }

  // Pattern check
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    return 'Invalid format';
  }

  // Min value check
  if (rules.min !== undefined && Number(value) < rules.min) {
    return `Minimum value is ${rules.min}`;
  }

  // Max value check
  if (rules.max !== undefined && Number(value) > rules.max) {
    return `Maximum value is ${rules.max}`;
  }

  // Custom validation
  if (rules.custom) {
    return rules.custom(value);
  }

  return null;
}

export function validateForm(
  data: Record<string, unknown>,
  rules: Record<string, ValidationRule>
): ValidationErrors {
  const errors: ValidationErrors = {};

  Object.keys(rules).forEach((field) => {
    const error = validateField(data[field], rules[field]);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
}

// Common validators
export const emailValidator: ValidationRule = {
  required: true,
  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  custom: (value) => {
    const stringValue = String(value);
    if (stringValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stringValue)) {
      return 'Invalid email address';
    }
    return null;
  },
};

export const passwordValidator: ValidationRule = {
  required: true,
  minLength: 8,
  custom: (value) => {
    const stringValue = String(value);
    if (stringValue.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/(?=.*[a-z])/.test(stringValue)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(stringValue)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(stringValue)) {
      return 'Password must contain at least one number';
    }
    return null;
  },
};

export const urlValidator: ValidationRule = {
  pattern: /^https?:\/\/.+/,
  custom: (value) => {
    const stringValue = String(value);
    if (stringValue && !/^https?:\/\/.+/.test(stringValue)) {
      return 'Invalid URL';
    }
    return null;
  },
};

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isValidPassword(password: string): boolean {
  return (
    password.length >= 8 &&
    /(?=.*[a-z])/.test(password) &&
    /(?=.*[A-Z])/.test(password) &&
    /(?=.*\d)/.test(password)
  );
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
